import React, { useState } from 'react';
import { Box, Grid, IconButton, Paper, TablePagination, Typography } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';

import { ZoneCategoryCreateDialog, ZoneCategoryDeleteDialog, ZoneCategoryUpdateDialog } from 'dialogs/zone-category';
import { useZoneCategories } from 'store/apis';
import { CrystalButton, Loading } from 'components';

export const ZoneCategoriesPage = () => {
  const { data: categories, isLoading: loading_categories } = useZoneCategories();
  const [
    { visibleZoneCategoryCreateDialog, visibleZoneCategoryUpdateDialog, visibleZoneCategoryDeleteDialog, categoryId },
    setState,
  ] = useState<{
    visibleZoneCategoryCreateDialog: boolean;
    visibleZoneCategoryUpdateDialog: boolean;
    visibleZoneCategoryDeleteDialog: boolean;
    categoryId?: number;
  }>({
    visibleZoneCategoryCreateDialog: false,
    visibleZoneCategoryUpdateDialog: false,
    visibleZoneCategoryDeleteDialog: false,
  });
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(5);
  const [page, setPage] = React.useState(0);

  const setVisibleZoneCategoryCreateDialog = (visibleZoneCategoryCreateDialog: boolean) =>
    setState((prev) => ({ ...prev, visibleZoneCategoryCreateDialog }));
  const setVisibleZoneCategoryUpdateDialog = (visibleZoneCategoryUpdateDialog: boolean) =>
    setState((prev) => ({ ...prev, visibleZoneCategoryUpdateDialog }));
  const setVisibleZoneCategoryDeleteDialog = (visibleZoneCategoryDeleteDialog: boolean) =>
    setState((prev) => ({ ...prev, visibleZoneCategoryDeleteDialog }));
  const setCategoryId = (categoryId?: number) => setState((prev) => ({ ...prev, categoryId }));

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCategories = categories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <Typography variant="h5">Zone Categories</Typography>

        <CrystalButton onClick={() => setVisibleZoneCategoryCreateDialog(true)} sx={{ marginLeft: 'auto' }} />
      </Box>

      {loading_categories && <Loading text="Loading zone categories..." height={320} />}

      {!loading_categories && (
        <>
          {categories.length === 0 && (
            <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="textSecondary">No zone category</Typography>
            </Box>
          )}

          {categories.length > 0 && (
            <>
              <Box sx={{ minHeight: 320 }}>
                <Grid container spacing={2}>
                  {paginatedCategories.map(({ id, title }, index) => (
                    <Grid item xs={12} key={`category-${index}`}>
                      <ZoneCategoryCard
                        title={title}
                        onEdit={() => {
                          setCategoryId(id);
                          setVisibleZoneCategoryUpdateDialog(true);
                        }}
                        onDelete={() => {
                          setCategoryId(id);
                          setVisibleZoneCategoryDeleteDialog(true);
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box sx={{ paddingTop: 2, display: 'flex', justifyContent: 'center' }}>
                <TablePagination
                  rowsPerPageOptions={[
                    {
                      value: 5,
                      label: '5',
                    },
                    {
                      value: 10,
                      label: '10',
                    },
                    {
                      value: 20,
                      label: '20',
                    },
                  ]}
                  component="div"
                  count={categories.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  labelRowsPerPage={'Items per Page'}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            </>
          )}
        </>
      )}

      <ZoneCategoryCreateDialog
        visible={visibleZoneCategoryCreateDialog}
        setVisible={setVisibleZoneCategoryCreateDialog}
      />

      {categoryId && (
        <ZoneCategoryUpdateDialog
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          visible={visibleZoneCategoryUpdateDialog}
          setVisible={setVisibleZoneCategoryUpdateDialog}
        />
      )}

      {categoryId && (
        <ZoneCategoryDeleteDialog
          categoryId={categoryId}
          setCategoryId={setCategoryId}
          visible={visibleZoneCategoryDeleteDialog}
          setVisible={setVisibleZoneCategoryDeleteDialog}
        />
      )}
    </>
  );
};

interface ZoneCategoryCardProps {
  title: string;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ZoneCategoryCard = ({ title, onEdit = () => {}, onDelete = () => {} }: ZoneCategoryCardProps) => {
  return (
    <Paper sx={{ paddingY: 2, paddingLeft: 2, paddingRight: 1.5 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item flex={1}>
          <Typography>{title}</Typography>
        </Grid>

        <Grid item>
          <IconButton color="primary" size="small" onClick={onEdit}>
            <Edit sx={{ fontSize: 20 }} />
          </IconButton>

          <IconButton color="primary" size="small" onClick={onDelete} sx={{ marginLeft: 0.5 }}>
            <Delete sx={{ fontSize: 20 }} />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
};
